import React from 'react'
import createRouter5, { Route, Router, State, NavigationOptions } from 'router5'
import { useRoute } from 'react-router5'
import browserPlugin from 'router5-plugin-browser'
import { User } from './types/app'

export interface IApplicationRouter {
  router: Router,
  getApplicationRoute: () => IApplicationRoute,
  RenderPage: () => JSX.Element,
  initialize: (user?: User) => Promise<State>,
  setUser: (user?: User) => void,
  startMiddleware: () => void,
  setInitializing: (initializing: boolean) => void,
}

export interface IRouteRedirect {
  name: string,
  params?: Record<string, any>,
}

export type IRouteProcessor = (user: User | undefined, fromState: State | null, toState: State) => IRouteRedirect | undefined

export interface IApplicationRoute extends Route {
  render: () => JSX.Element,
  meta: Record<string, any>,
  processRoute?: IRouteProcessor,
}

export interface IRouterInitializedEvent {
  postLoginRoute?: {
    name: string,
    params: Record<string, string>,
  },
  loadCustomerAndJob?: {
    customer?: string,
    job?: string,
  }
}

// Default not found route that can be overridden
export const DEFAULT_NOT_FOUND_ROUTE: IApplicationRoute = {
  name: 'notfound',
  path: '/notfound',
  meta: {
    title: 'Page Not Found',
  },
  render: () => <div>Page Not Found</div>,
}

// Configuration options for router creation
export interface RouterConfig {
  notFoundRoute?: IApplicationRoute
  defaultRouteName?: string
  queryParamsMode?: 'loose' | 'strict'
}

// Main function to create and configure the router
export function createRouter(routes: IApplicationRoute[], config?: RouterConfig): IApplicationRouter {
  const {
    notFoundRoute = DEFAULT_NOT_FOUND_ROUTE,
    defaultRouteName = 'notfound',
    queryParamsMode = 'loose',
  } = config || {}

  let currentUser: User | undefined
  let isInitializing = false

  // Combine application routes with not found route
  const allRoutes = [...routes, notFoundRoute]

  // Create and configure the router
  const router = createRouter5(allRoutes, {
    defaultRoute: defaultRouteName,
    queryParamsMode,
  })

  const getApplicationRoute = (): IApplicationRoute => {
    const { route } = useRoute()
    return allRoutes.find(r => r.name === route?.name) || notFoundRoute
  }

  const RenderPage = () => {
    const route = getApplicationRoute()
    return route.render()
  }

  const setUser = async (user?: User) => {
    currentUser = user
  }

  const setInitializing = (initializing: boolean) => {
    isInitializing = initializing
  }

  const buildStateForRedirect = (redirect: IRouteRedirect, baseState: State): State | null => {
    const params = redirect.params ?? baseState.params ?? {}
    const nextState = router.buildState(redirect.name, params)
    if (!nextState) return null
    const path = router.buildPath(nextState.name, nextState.params)
    return router.makeState(nextState.name, nextState.params, path, {
      params: nextState.meta,
      options: baseState.meta?.options,
    })
  }

  const resolveProcessRoute = (initialState: State, fromState: State | null): State => {
    let currentState = initialState
    let redirectCount = 0

    while (redirectCount < 10) {
      const destRoute = allRoutes.find(r => r.name === currentState.name)
      if (!destRoute?.processRoute) break

      const redirect = destRoute.processRoute(currentUser, fromState, currentState)
      if (!redirect) break

      // During initialization, skip requireUser redirects so we remember the target route
      if (isInitializing && redirect.name === 'home' && currentState.name !== 'home') {
        break
      }

      const nextState = buildStateForRedirect(redirect, currentState)
      if (!nextState) {
        break
      }

      currentState = nextState
      redirectCount += 1
    }

    return currentState
  }

  const originalTransitionToState = router.transitionToState.bind(router) as (toState: State, fromState: State | null, options: NavigationOptions, done: Parameters<Router['transitionToState']>[3]) => any

  router.transitionToState = ((toState: State, fromState: State | null, options: NavigationOptions, done) => {
    const processedState = resolveProcessRoute(toState, fromState)
    return originalTransitionToState(processedState, fromState, options, done)
  }) as Router['transitionToState']

  const initialize = async (user?: User): Promise<State> => {
    setUser(user)
    isInitializing = true

    return new Promise((resolve) => {
      router.start(async () => {
        resolve(router.getState())
      })
    })
  }

  const startMiddleware = () => {
    isInitializing = false
  }

  router.usePlugin(browserPlugin())
  

  return {
    router,
    getApplicationRoute,
    RenderPage,
    initialize,
    setUser,
    startMiddleware,
    setInitializing,
  }
}

export default createRouter
